import { useState } from "react";
import { X, Mail, Lock, User, Loader2, CheckCircle } from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { API } from "../lib/constants";
import { postJsonResilient } from "../lib/httpClient";

export default function AuthModal({ onClose }) {
  const { login, register } = useUser();
  const [mode, setMode] = useState("login"); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "register") {
        if (!name.trim()) { setError("Name is required"); setSubmitting(false); return; }
        await register(email, password, name);
        onClose();
      } else if (mode === "forgot") {
        const { ok, data } = await postJsonResilient(`${API}/auth/forgot-password`, { email });
        if (!ok) {
          setError(data.detail || "Something went wrong. Please try again.");
          return;
        }
        setForgotSent(true);
      } else {
        await login(email, password);
        onClose();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setForgotSent(false);
  };

  const titleMap = {
    login: "Welcome Back",
    register: "Create Account",
    forgot: "Reset Password",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="auth-modal">
      <div className="w-full max-w-md mx-4 bg-[#111] border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-6 pb-0 flex items-center justify-between">
          <h2 className="font-cinzel text-xl font-bold">{titleMap[mode]}</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg transition-colors" data-testid="close-auth-modal">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {mode === "forgot" && forgotSent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-500" />
              </div>
              <p className="text-sm text-gray-300 mb-2">If an account exists for <strong className="text-white">{email}</strong>, a password reset link has been sent.</p>
              <p className="text-xs text-gray-500 mb-6">Check your inbox (and spam folder). The link expires in 1 hour.</p>
              <button
                onClick={() => switchMode("login")}
                className="text-sm text-red-400 hover:text-red-300 font-semibold"
                data-testid="forgot-back-to-login"
              >
                ← Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "register" && (
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                    className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" required
                  className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
                  data-testid="auth-email-input"
                />
              </div>
              {mode !== "forgot" && (
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required minLength={6}
                    className="w-full pl-10 pr-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
                    data-testid="auth-password-input"
                  />
                </div>
              )}
              {error && <p className="text-red-400 text-sm" data-testid="auth-error">{error}</p>}
              {mode === "login" && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="text-xs text-gray-400 hover:text-red-400 font-semibold transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              <button
                type="submit" disabled={submitting}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all disabled:opacity-50"
                data-testid="auth-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mx-auto" /> :
                  mode === "login" ? "Sign In" :
                  mode === "register" ? "Create Account" :
                  "Send Reset Link"}
              </button>
            </form>
          )}

          {!forgotSent && (
            <p className="text-center text-sm text-gray-500">
              {mode === "forgot" ? (
                <>
                  Remembered your password?{" "}
                  <button onClick={() => switchMode("login")} className="text-red-400 hover:text-red-300 font-semibold" data-testid="auth-back-to-login">
                    Sign In
                  </button>
                </>
              ) : mode === "login" ? (
                <>
                  Don't have an account?{" "}
                  <button onClick={() => switchMode("register")} className="text-red-400 hover:text-red-300 font-semibold" data-testid="auth-toggle-mode">
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button onClick={() => switchMode("login")} className="text-red-400 hover:text-red-300 font-semibold" data-testid="auth-toggle-mode">
                    Sign In
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
